import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
  X, AlertCircle, CheckCircle, Loader2,
  User, MapPin, Briefcase, Heart, Settings,
  ChevronRight, ChevronLeft, Upload, Plus, Trash2, CreditCard,
} from 'lucide-react';
import { fetchAllEmployees } from '../../store/slices/employeeSlice';
import { checkTinUnique } from '../../utils/tinUnique';
import { RELATIONSHIP_OPTS } from '../../constants/relationshipOptions';
import { NEW_EMPLOYEE_IS_APPROVED } from '../../utils/employeeApproval';
import axios from 'axios';

// ─── Services ─────────────────────────────────────────────────────────────────
import employeeService from '../../services/EmployeeService';
import { clearAuthedFileCache } from '../../utils/authedFile';
import regionService from '../../services/RegionService';
import { RG, rgFilterDistricts, rgFilterElectorates, rgStationOptions, isContractCategory } from '../../utils/employeeUtils';
import designationService from '../../services/DesignationService';
import departmentService from '../../services/DepartmentService';
import divisionService from '../../services/DivisionService';
import locationService from '../../services/LocationService';
import gradeService from '../../services/GradeService';
import shiftService from '../../services/ShiftService';
import employeeCategoryService from '../../services/EmployeeCategoryService';
import bankService from '../../services/BankService';
import bankBranchService from '../../services/BankBranchService';
import { getLoggedInServiceNo } from '../../utils/masterDataUtils';
import { contactTypeFor, CONTACT_TYPE_CONTACT } from '../../utils/contactType';
import { isMasterApproved } from '../../utils/masterApproval';
import { NEW_BANK_IS_APPROVED } from '../../utils/employeeBankApproval';
import IsoDateInput from '../common/Isodateinput';
import SearchableSelect from '../common/SearchableSelect';
import ExperienceInput from '../common/Experienceinput';
import {
  sanitiseStaffId, checkStaffId,
  sanitiseEpf, checkEpf,
  sanitiseNic, normaliseNic, checkNic,
  checkDateOfBirth, maxDobIso,
  checkDateOfJoining, todayIso,
  sanitisePhone, checkPhone, checkMobile,
  sanitiseDecimal,
  checkEmail,
  sanitisePostal, checkPostal,
  parseExperience, checkExperience,
} from '../../utils/employeeValidation';

/* Sent as p_hebAccountType. Savings is preselected. */
const ACCOUNT_TYPES = [
  { value: 'Savings', label: 'Savings' },
  { value: 'Current', label: 'Current' },
];
const DEFAULT_ACCOUNT_TYPE = 'Savings';


// ─── Static Options ───────────────────────────────────────────────────────────
const TITLES          = ['Mr', 'Mrs', 'Ms', 'Miss', 'Dr', 'Rev'];
const GENDERS         = [{ value: 'M', label: 'Male' }, { value: 'F', label: 'Female' }];
const MARITAL_STATUSES = [
  { value: 'S', label: 'Single' }, { value: 'M', label: 'Married' },
  { value: 'D', label: 'Divorced' }, { value: 'W', label: 'Widowed' },
];

const RELIGIONS = [
  { value: 'B', label: 'Buddhist' }, { value: 'C', label: 'Christian' },
  { value: 'H', label: 'Hindu' }, { value: 'I', label: 'Islam' }, { value: 'O', label: 'Other' },
];
const BLOOD_GROUPS = [
  { value: 1, label: 'A+' }, { value: 2, label: 'A-' }, { value: 3, label: 'B+' },
  { value: 4, label: 'B-' }, { value: 5, label: 'AB+' }, { value: 6, label: 'AB-' },
  { value: 7, label: 'O+' }, { value: 8, label: 'O-' },
];

const OT_TYPES = [
  {
    value: "Fixed",
    label: "Fixed"
  },
  {
    value: "Variable",
    label: "Variable"
  }
]

const EMPLOYEE_TYPES = [
  {
    value: "Field",
    label: "Field Staff"
  },
  {
    value: "Staff",
    label: "Office Staff"
  }
];

// ─── Tabs ─────────────────────────────────────────────────────────────────────
const TABS = [
  { id: 0, icon: User,      label: 'Personal'    },
  { id: 1, icon: MapPin,    label: 'Address'     },
  { id: 2, icon: Briefcase, label: 'Employment'  },
  { id: 3, icon: Heart,     label: 'Contact & Emergency' },
  { id: 4, icon: CreditCard, label: 'Bank'       },
  { id: 5, icon: Settings,  label: 'Other'       },
];

// ─── Empty Form ───────────────────────────────────────────────────────────────
const EMPTY = {
  serviceNo: '', epfNo: '', etfNo: '', tinNo: '', nicNo: '', passportNo: '',
  // Region (new section on the Address step)
  regionProvince: '', regionDistrict: '', regionElectorate: '', regionPoliceStation: '',
  regionGrama: '',
  title: 'Mr', gender: 'M',
  firstName: '', middleName: '', lastName: '', initials: '', reportName: '',
  dateOfBirth: '', maritalStatus: 'S', religion: '', bloodGroup: '', noOfChildren: '', distance: '',
  mobileNo: '', homeNo: '', officeNo: '', personalEmail: '', companyEmail: '',
  permanentAddressLine1: '', permanentAddressLine2: '', permanentAddressLine3: '', permanentAddressCity: '', permanentAddressPostal: '',
  currentAddressLine1: '', currentAddressLine2: '', currentAddressLine3: '', currentAddressCity: '', currentAddressPostal: '',
  designationId: '', divisionCode: '', locationId: '', gradeId: '', employmentCategoryId: '',
  shiftId: '', otType: '', employeeType: '', dateOfJoining: '', noticePeriodDays: '', otRate: '', totalExperience: '',
  recruitmentDate: '', promotedDate: '', permanentDate: '', retirementDate: '',
  terminationDate: '',
  reportingEmployeeId: '', costCenter: '', group: '',
  bankId: '', branchId: '', accountNumber: '',
  remarks: '',
};


const newContact = (id) => ({ id, name: '', relationship: '', number: '', address: '', isEmergency: false });
const contactFilled = (c) => !!(fmt(c.name) || fmt(c.number));

const EMPTY_FILES = { profilePic: null, birthCertificate: null, nicDocument: null, qualificationCertificate: null, traineeCertificate: null, otherCertificate: null, jobDescription: null };

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (v) => (!v || v === 'null') ? '' : String(v).trim();

const extractList = (res) => { 
  if (!res) return [];
  const data = res?.data ?? res;
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.ResultSet)) return data.ResultSet;
  if (Array.isArray(data.resultSet)) return data.resultSet;
  if (Array.isArray(data.Result)) return data.Result;
  if (Array.isArray(data.result)) return data.result;
  if (Array.isArray(data.Data)) return data.Data;
  if (Array.isArray(data.data)) return data.data;
  return [];
};

const pick = (obj, hints, fallback = '') => {
  if (!obj || typeof obj !== 'object') return fallback;
  
  for (const h of hints) {
    if (obj[h] !== undefined && obj[h] !== null && obj[h] !== '') return obj[h];
  }

  const objKeys = Object.keys(obj).map(k => ({ raw: k, norm: String(k).replace(/[^a-zA-Z0-9]/g, '').toUpperCase() }));
  for (const h of hints) {
    const hNorm = String(h).replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    if (!hNorm) continue;
    let match = objKeys.find(k => k.norm === hNorm);
    if (!match) match = objKeys.find(k => k.norm.endsWith(hNorm));
    if (!match && hNorm.length >= 3) match = objKeys.find(k => k.norm.includes(hNorm));
    if (match && obj[match.raw] !== undefined && obj[match.raw] !== null && obj[match.raw] !== '') {
      return obj[match.raw];
    }
  }
  return fallback;
};

// Employee Category dropdown: only categories with HEC_IS_ACTIVE true
// (also accepts "True" / 1 in case the API sends them that way).
const isCategoryActive = (c) => {
  const v = c?.HEC_IS_ACTIVE;
  if (v === undefined || v === null) return true;   // field missing → don't hide it
  return v === true || v === 1 || /^(true|1)$/i.test(String(v).trim());
};

// Division dropdown: only HDD_CURRENT_STATUS "A" divisions, shown by HDD_DIV_DESC.
const toDivisionOpts = (list) => (Array.isArray(list) ? list : [])
  .filter((r) => String(r?.HDD_CURRENT_STATUS ?? '').trim().toUpperCase() === 'A')
  .map((r) => ({ value: String(r.HDD_DIV_CODE ?? '').trim(), label: String(r.HDD_DIV_DESC || r.HDD_DIV_CODE || '').trim() }))
  .filter((o) => o.value);

// Department rows: only HDD_IS_ACTIVE true, each tagged with its division
// (HDD_DIV_CODE) so the dropdown can show just the chosen division's ones.
const isDeptActive = (r) => {
  const v = r?.HDD_IS_ACTIVE;
  if (v === undefined || v === null) return true;
  return v === true || v === 1 || /^(true|1)$/i.test(String(v).trim());
};

const toOpts = (list, idKeys = [], nameKeys = []) => {
  if (!Array.isArray(list)) return [];
  return list.map(row => {
    if (!row || typeof row !== 'object') return null;
    const id = pick(row, [...idKeys, 'Id', 'id', 'Code', 'code', 'ID', 'KEY', 'key']);
    const name = pick(row, [...nameKeys, 'Name', 'name', 'Description', 'description', 'Desc', 'desc', 'Title', 'title', 'LABEL', 'label']);
    if (id === undefined || id === null || id === '') return null;
    return { value: String(id), label: String(name || id) };
  }).filter(Boolean);
};


const safeFetch = async (primaryFn, fallbackUrl) => {
  try {
    const res = await primaryFn();
    const list = extractList(res);
    if (list && list.length > 0) return list;
  } catch (e) {}
  if (fallbackUrl) {
    try {
      const res = await axios.get(fallbackUrl);
      const list = extractList(res);
      if (list && list.length > 0) return list;
    } catch (e) {}
  }
  return [];
};

const extraAddressTypeCode = (index) => String.fromCharCode('D'.charCodeAt(0) + index);

// ─── UI Atoms ─────────────────────────────────────────────────────────────────
function Toast({ message, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 4500); return () => clearTimeout(t); }, [onClose]);
  const cfg = {
    error:   { bg: 'bg-red-600',     icon: <AlertCircle size={16} /> },
    success: { bg: 'bg-emerald-600', icon: <CheckCircle size={16} /> },
    warning: { bg: 'bg-amber-500',   icon: <AlertCircle size={16} /> },
  };
  const c = cfg[type] || cfg.error;
  return (
    <div className={`fixed top-4 right-4 z-[9999] flex items-center gap-3 px-4 py-3 rounded-lg shadow-2xl text-white text-sm font-medium max-w-xs ${c.bg}`}>
      {c.icon}<span className="flex-1">{message}</span>
      <button onClick={onClose} className="opacity-70 hover:opacity-100 ml-1"><X size={14} /></button>
    </div>
  );
}

function Field({ label, required, error, children }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[11px] font-medium text-gray-500">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
      {error && <span className="text-[10px] text-red-500 flex items-center gap-0.5 mt-0.5"><AlertCircle size={9} />{error}</span>}
    </div>
  );
}

/* One field shape for the whole app: 36px tall, rounded-lg, gray-200 border,
   primary focus. Same as the master-file entry panels and payroll toolbars. */
const iCls = (err) =>
  `w-full h-9 border rounded-lg px-2.5 text-xs outline-none transition-colors
   ${err ? 'border-red-300 bg-red-50 focus:border-red-400' : 'border-gray-200 bg-white text-gray-800 focus:border-primary-500'}`;

function Inp({ value, onChange, placeholder, type = 'text', error, id }) {
  return <input id={id} type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} className={iCls(error)} />;
}

// function Sel({ value, onChange, options = [], placeholder, error, loading, id }) {
//   return (
//     <div className="relative">
//       <select id={id} value={value || ''} onChange={e => onChange(e.target.value)} className={`${iCls(error)} appearance-none pr-6`} disabled={loading}>
//         <option value="">{loading ? 'Loading...' : (placeholder || '— Select —')}</option>
//         {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
//       </select>
//       <svg className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" width="12" height="12" viewBox="0 0 12 12" fill="none">
//         <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
//       </svg>
//     </div>
//   );
// }
/**
 * @param {boolean} [clearable=true]  When false the "— Select —" row is not
 *   offered, so the field can never be emptied back to null. Use it for fields
 *   that always carry a value (account type, for instance).
 */
function Sel({ value, onChange, options = [], placeholder, error, loading, id, clearable = true }) {
  // Thin wrapper over the shared SearchableSelect. Search shows once the list
  // has more than 5 options, as before.
  const emptyLabel = placeholder || '— Select —';
  return (
    <SearchableSelect
      id={id}
      value={value}
      onChange={onChange}
      options={options}
      emptyOption={clearable ? { value: '', label: emptyLabel } : null}
      placeholder={emptyLabel}
      loading={loading}
      searchMinOptions={6}
      className={`${iCls(error)} appearance-none bg-white`}
    />
  );
}

function FileField({ label, file, onChange, accept = 'image/*,.pdf', error }) {
  const inputId = `file-${label.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <div className="flex flex-col gap-0.5">
      <label className="text-[11px] font-semibold text-gray-600">{label}</label>
      <label
        htmlFor={inputId}
        className={`flex items-center justify-between gap-2 border rounded px-2.5 py-1.5 text-[13px] bg-white cursor-pointer hover:bg-gray-50 transition-colors
          ${error ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
      >
        <span className={`truncate ${file ? 'text-gray-700' : 'text-gray-400'}`}>
          {file ? file.name : 'Choose file...'}
        </span>
        <Upload size={13} className="text-gray-400 shrink-0" />
      </label>
      <input
        id={inputId}
        type="file"
        accept={accept}
        className="hidden"
        onChange={e => onChange(e.target.files?.[0] || null)}
      />
      {file && (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="text-[10px] text-red-500 hover:text-red-600 text-left w-fit"
        >
          Remove file
        </button>
      )}
      {error && <span className="text-[10px] text-red-500 flex items-center gap-0.5 mt-0.5"><AlertCircle size={9} />{error}</span>}
    </div>
  );
}

function SecLabel({ label }) {
  return (
    <div className="flex items-center gap-2 mb-2 mt-1">
      <span className="text-[11px] font-bold text-primary-700 uppercase tracking-wider">{label}</span>
      <div className="flex-1 h-px bg-primary-200" />
    </div>
  );
}

function G2({ children }) { return <div className="grid grid-cols-2 gap-x-4 gap-y-3">{children}</div>; }
function G3({ children }) { return <div className="grid grid-cols-3 gap-x-4 gap-y-3">{children}</div>; }
function G4({ children }) { return <div className="grid grid-cols-4 gap-x-4 gap-y-3">{children}</div>; }

// ─── Main Component ───────────────────────────────────────────────────────────
export default function AddEmployeeForm({ onClose, onSuccess }) {
  const dispatch = useDispatch();

  const [tab, setTab] = useState(0);
  const [visitedTabs, setVisited] = useState(new Set([0]));
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState(null);
  const [submitting, setSub] = useState(false);
  const [apiError, setApiError] = useState(null);

  const [files, setFiles] = useState(EMPTY_FILES);
  const setFile = (key, file) => setFiles(f => ({ ...f, [key]: file }));

  const [extraAddresses, setExtraAddresses] = useState([]);
  const addExtraAddress = () => setExtraAddresses(list => ([
    ...list,
    { id: `addr-${Date.now()}-${list.length}`, label: '', line1: '', line2: '', line3: '', city: '', postal: '' },
  ]));
  const removeExtraAddress = (id) => setExtraAddresses(list => list.filter(a => a.id !== id));
  const updateExtraAddress = (id, key, value) =>
    setExtraAddresses(list => list.map(a => a.id === id ? { ...a, [key]: value } : a));

  // Contacts — any number per employee. Each is saved as 'Contact' unless
  // ticked as an emergency contact, which saves it as 'Emergency'.
  const [contacts, setContacts] = useState([newContact('contact-0')]);
  const addContact = () => setContacts(list => ([
    ...list,
    newContact(`contact-${Date.now()}-${list.length}`),
  ]));
  const removeContact = (id) => setContacts(list => list.filter(c => c.id !== id));
  const updateContact = (id, key, value) =>
    setContacts(list => list.map(c => c.id === id ? { ...c, [key]: value } : c));

  const [qualifications, setQualifications] = useState([
    { id: 'qual-0', name: '', fieldOfStudy: '', institute: '', result: '', grade: '', gpa: '' }
  ]);
  const addQualification = () => setQualifications(list => ([
    ...list,
    { id: `qual-${Date.now()}-${list.length}`, name: '', fieldOfStudy: '', institute: '', result: '', grade: '', gpa: '' }
  ]));
  const removeQualification = (id) => setQualifications(list => list.filter(q => q.id !== id));
  const updateQualification = (id, key, value) =>
    setQualifications(list => list.map(q => q.id === id ? { ...q, [key]: value } : q));

  const [bankAccounts, setBankAccounts] = useState([
    { id: 'bank-0', bankId: '', branchId: '', accountNumber: '', accountType: DEFAULT_ACCOUNT_TYPE, isPrimary: true }
  ]);
  const addBankAccount = () => setBankAccounts(list => ([
    ...list,
    { id: `bank-${Date.now()}-${list.length}`, bankId: '', branchId: '', accountNumber: '', accountType: DEFAULT_ACCOUNT_TYPE, isPrimary: list.length === 0 }
  ]));
  // Removing the primary account hands "primary" to the first one left.
  const removeBankAccount = (id) => setBankAccounts(list => {
    const left = list.filter(b => b.id !== id);
    if (left.length && !left.some(b => b.isPrimary)) left[0] = { ...left[0], isPrimary: true };
    return left;
  });
  const updateBankAccount = (id, key, value) =>
    setBankAccounts(list => list.map(b => {
      // Only one primary account: ticking one unticks the rest.
      if (key === 'isPrimary' && value === true) return { ...b, isPrimary: b.id === id };
      return b.id === id ? { ...b, [key]: value } : b;
    }));

  // ─── Dropdown data ────────────────────────────────────────────────────────
  const [ddLoading, setDdLoading] = useState(true);
  const [designations, setDesignations]   = useState([]);
  const [ departments , setDepartments] = useState([]);   // active departments, { value, label, div }
  const [divisions,    setDivisions]      = useState([]);
  const [locations,    setLocations]      = useState([]);
  const [grades,       setGrades]         = useState([]);
  const [shifts,       setShifts]         = useState([]);
  const [categories,   setCategories]     = useState([]);
  const [banks,        setBanks]          = useState([]);
  const [branches,     setBranches]       = useState([]);
  const [employees,    setEmployees]      = useState([]); 
  // Region master data — raw rows, so the cascade can read parent codes.
  const [rgProvinces,   setRgProvinces]   = useState([]);
  const [rgDistricts,   setRgDistricts]   = useState([]);
  const [rgElectorates, setRgElectorates] = useState([]);
  const [rgStations,    setRgStations]    = useState([]);
  const [rgGramas,      setRgGramas]      = useState([]);

  const showToast = useCallback((msg, type = 'error') => setToast({ message: msg, type }), []);

  const set = (key, val) => {
    setForm(f => ({ ...f, [key]: val }));
    setErrors(e => ({ ...e, [key]: undefined }));
    if (key === 'bankId') setForm(f => ({ ...f, bankId: val, branchId: '' }));
    // New division → its departments differ, so the department is cleared.
    if (key === 'divisionCode') {
      setForm(f => ({ ...f, divisionCode: val, departmentId: '' }));
      setErrors(e => ({ ...e, departmentId: undefined }));
    }
  };

  // ─── Load all dropdowns with fallbacks via axios ───────────────────────────
  useEffect(() => {
    const token = localStorage.getItem('AuthKey') || localStorage.getItem('auth-key') || '';
    if (token) axios.defaults.headers.common['auth-key'] = token;

    setDdLoading(true);
    Promise.all([
      safeFetch(() => designationService.GetDesignationList(), 'EMP/GetDevDetails'),
      safeFetch(() => departmentService.GetDepartmentList(), 'EMP/GetDepDetails'),
      safeFetch(() => locationService.GetLocationList(), 'EMP/GetLocDetails'),
      safeFetch(() => gradeService.GetGradeList(), 'EMP/GetProfDetails'),
      safeFetch(() => shiftService.GetShiftList(), 'EMP/GetShiftTypes'),
      safeFetch(() => employeeCategoryService.GetEmployeeCategoryList(), 'EMP/GetEMPCat'),
      safeFetch(() => bankService.GetBankList(), 'BankDetails/GetBanks'),
      safeFetch(() => bankBranchService.getBankBranchList(), 'BankDetails/GetAllBranch'),
      safeFetch(() => employeeService.GetEmployeeList(), 'EMP/GetEmployees'),
      safeFetch(() => regionService.GetProvinceList(),      'Region/LoadProvinceDetails'),
      safeFetch(() => regionService.GetDistrictList(),      'Region/LoadDistrictDetails'),
      safeFetch(() => regionService.GetElectorateList(),    'Region/LoadElectorateDetails'),
      safeFetch(() => regionService.GetPoliceStationList(), 'Region/LoadPolicestationDetails'),
      safeFetch(() => regionService.GetGramaList(),         'Region/GetAllGrama'),
      safeFetch(() => divisionService.GetDivisions(),       'Division/GetDivisions'),
    ]).then(([desig, dept, loc, grade, shift, cat, bank, branch, emp,
              rgProv, rgDist, rgElec, rgStn, rgGrama, div]) => {
      setDivisions(toDivisionOpts(div));
      setRgProvinces(Array.isArray(rgProv) ? rgProv : []);
      setRgDistricts(Array.isArray(rgDist) ? rgDist : []);
      setRgElectorates(Array.isArray(rgElec) ? rgElec : []);
      setRgStations(Array.isArray(rgStn) ? rgStn : []);
      setRgGramas(Array.isArray(rgGrama) ? rgGrama : []);
      setDesignations(toOpts(desig,
        ['DesignationId','designationId','HDS_DESIGNATION_ID','HDS_DESIGNATION_CODE','p_des_code','DesignationCode'],
        ['DesignationName','designationName','HDS_DESIGNATION_NAME','HDS_DESIGNATION_DESC','p_des_name','DesignationDesc']
      ));
      setDepartments((Array.isArray(dept) ? dept : []).filter(isDeptActive).map(row => {
        const [opt] = toOpts([row],
          ['DepartmentId','departmentId','HDD_DEPARTMENT_ID','HDD_DEP_ID','HDD_DEP_CODE','p_department_code'],
          ['DepartmentName','departmentName','HDD_DEPARTMENT_NAME','HDD_DEP_NAME','HDD_DEP_DESC','p_department_name']
        );
        return opt ? { ...opt, div: String(row.HDD_DIV_CODE ?? '').trim() } : null;
      }).filter(Boolean));
      setLocations(   toOpts(loc,
        ['LocationId','locationId','HLD_LOCATION_ID','HLD_LOC_ID','HLD_LOC_CODE','p_location_code','p_loc_code'],
        ['LocationName','locationName','HLD_LOCATION_NAME','HLD_LOC_NAME','HLD_LOC_DESC','p_location_name','p_loc_name']
      ));
      setGrades(      toOpts(grade,
        ['GradeId','gradeId','HGC_GRADE_ID','HGC_GRADE_CODE','p_grade_code','p_initial_grade'],
        ['GradeName','gradeName','HGC_GRADE_NAME','HGC_GRADE_DESC','p_grade_name']
      ));
      setShifts(      toOpts(shift,
        ['ShiftId','shiftId','HSC_SHIFT_ID','HSC_SHIFT_CODE','p_shift_code'],
        ['ShiftName','shiftName','HSC_SHIFT_NAME','HSC_SHIFT_DESC','p_shift_name']
      ));
      setCategories(  toOpts((Array.isArray(cat) ? cat : []).filter(isCategoryActive),
        ['HEC_EMPLOYMENT_CATEGORY_ID','CategoryId','categoryId','HEC_CATEGORY_ID','HEC_CATEGORY_CODE','p_cat_code'],
        ['CategoryName','categoryName','HEC_CATEGORY_NAME','HEC_CATEGORY_DESC','p_cat_name']
      ));
      // Only approved master records (HBD_IS_APPROVED / HBB_IS_APPROVED = "1")
      // may be picked here — pending banks/branches stay out of the dropdowns.
      const approvedBanks    = (bank   || []).filter(isMasterApproved);
      const approvedBranches = (branch || []).filter(isMasterApproved);

      setBanks(       toOpts(approvedBanks,
        ['HBD_BANK_ID','BankId','bankId','BankID','BankCode'],
        ['HBD_BANK_NAME','BankName','bankName','BankDesc']
      ));
      setBranches(    approvedBranches.map(b => {
        const id = pick(b, ['HBB_BRANCH_ID','BranchId','branchId','BranchID','BranchCode']);
        const name = pick(b, ['HBB_BRANCH_NAME','BranchName','branchName','BranchDesc']);
        const code = pick(b, ['HBB_BRANCH_CODE','BranchCode','branchCode']);
        const bankId = pick(b, ['HBB_BANK_ID','BankId','bankId','BankID']);
        return id ? { value: String(id), label: `${name || id} ${code ? `(${code})` : ''}`, bankId: String(bankId) } : null;
      }).filter(Boolean));
      setEmployees(   emp.map(e => {
        const id = pick(e, ['HedEmployeeId','EmployeeId','hedEmployeeId','employeeId','Id','id','HED_EMPLOYEE_ID']);
        const svcNo = pick(e, ['HedServiceNo','ServiceNo','hedServiceNo','serviceNo','HED_SERVICE_NO','p_ced_service_no']);
        const name = pick(e, ['HedReportName','ReportName','reportName','HED_REPORT_NAME','p_report_name']);
        const firstName = pick(e, ['HedFirstName','FirstName','firstName','HED_FIRST_NAME','p_first_name']);
        const lastName = pick(e, ['HedLastName','LastName','lastName','HED_LAST_NAME','p_last_name']);
        const fullName = name || `${firstName} ${lastName}`.trim();
        return id ? { value: String(id), label: `${fullName || id} ${svcNo ? `(${svcNo})` : ''}` } : null;
      }).filter(Boolean));
    }).finally(() => setDdLoading(false));
  }, []);

  const filteredBranches = form.bankId ? branches.filter(b => b.bankId === form.bankId) : branches;

  // Contract staff: Termination Date only; the four career dates are hidden.
  const isContract = isContractCategory(
    categories.find(c => String(c.value) === String(form.employmentCategoryId))?.label
  );

  // ─── Validation ───────────────────────────────────────────────────────────
const validate = () => {
  const e = {};
  const staffIdErr = checkStaffId(form.serviceNo);
  if (staffIdErr) e.serviceNo = staffIdErr;

  const epfErr = checkEpf(form.epfNo);
  if (epfErr) e.epfNo = epfErr;

  const dobErr = checkDateOfBirth(form.dateOfBirth);
  if (dobErr) e.dateOfBirth = dobErr;
  if (!fmt(form.firstName))      e.firstName      = 'First name is required';
  if (!fmt(form.lastName))       e.lastName       = 'Last name is required';
  // Initials are mandatory: they become the bank account holder name.
  if (!fmt(form.initials))       e.initials       = 'Initials are required';
  if (!form.designationId)       e.designationId  = 'Designation is required';
  if (!form.divisionCode)        e.divisionCode   = 'Division is required';
  if (!form.departmentId)        e.departmentId   = 'Department is required';
  if (!form.locationId)          e.locationId     = 'Location is required';
  if (!form.gradeId)             e.gradeId        = 'Grade is required';
  if (!form.employmentCategoryId) e.employmentCategoryId = 'Employment category is required';
  if (!form.shiftId)             e.shiftId        = 'Shift is required';
  const dojErr = checkDateOfJoining(form.dateOfJoining);
  if (dojErr) e.dateOfJoining = dojErr;

  // Contract staff must have a Termination Date.
  if (isContract && !fmt(form.terminationDate)) {
    e.terminationDate = 'Termination date is required for Contract employees';
  }

  const nicErr = checkNic(form.nicNo);
  if (nicErr) e.nicNo = nicErr;

  const personalEmailErr = checkEmail(form.personalEmail, 'Personal email');
  if (personalEmailErr) e.personalEmail = personalEmailErr;

  const companyEmailErr = checkEmail(form.companyEmail, 'Company email');
  if (companyEmailErr) e.companyEmail = companyEmailErr;

  // Every number field: digits only, at most 10.
  // Mobile is required and must be exactly 10 numbers.
  const mobileErr = checkMobile(form.mobileNo, 'Mobile no');
  if (mobileErr) e.mobileNo = mobileErr;
  const homeErr = checkPhone(form.homeNo, 'Home phone');
  if (homeErr) e.homeNo = homeErr;
  const officeErr = checkPhone(form.officeNo, 'Office phone');
  if (officeErr) e.officeNo = officeErr;
  contacts.forEach((c, i) => {
    const err = checkPhone(c.number, `Contact #${i + 1} number`);
    if (err) e[`contact_${c.id}_number`] = err;
  });

  /* A region is an attribute of an address row, so it has nowhere to be
     stored unless at least one address is filled in. */
  const hasAnyRegion = !!(form.regionProvince || form.regionDistrict ||
                          form.regionElectorate || form.regionPoliceStation ||
                          form.regionGrama);
  const hasAnyAddress = !!(form.permanentAddressLine1 || form.permanentAddressCity ||
                           form.currentAddressLine1 || form.currentAddressCity);
  if (hasAnyRegion && !hasAnyAddress) {
    e.permanentAddressLine1 =
      'Add a permanent or current address — the region is saved on the address';
  }

  const permPostalErr = checkPostal(form.permanentAddressPostal);
  if (permPostalErr) e.permanentAddressPostal = permPostalErr;
  const currPostalErr = checkPostal(form.currentAddressPostal);
  if (currPostalErr) e.currentAddressPostal = currPostalErr;

  const expParts = parseExperience(form.totalExperience);
  const expErr = checkExperience(expParts.years, expParts.months);
  if (expErr) e.totalExperience = expErr;

  // Exactly one primary account among the ones actually filled in.
  const filledBanks = bankAccounts.filter(b => b.bankId && b.branchId && fmt(b.accountNumber));
  const primaryCount = filledBanks.filter(b => b.isPrimary).length;
  if (primaryCount > 1) e.bankPrimary = 'Only one bank account can be primary';
  else if (filledBanks.length > 0 && primaryCount === 0) e.bankPrimary = 'Tick one bank account as primary';

  setErrors(e);
  if (Object.keys(e).length > 0) {
    showToast(Object.values(e)[0], 'error');
    
    const tabMap = {
      serviceNo: 0, epfNo: 0, etfNo: 0, dateOfBirth: 0, firstName: 0, lastName: 0, nicNo: 0,
      initials: 0,
      mobileNo: 0, homeNo: 0, officeNo: 0, personalEmail: 0, companyEmail: 0,
      designationId: 2, dateOfJoining: 2, totalExperience: 2, terminationDate: 2,
      divisionCode: 2, departmentId: 2, locationId: 2, gradeId: 2, employmentCategoryId: 2, shiftId: 2,
      permanentAddressLine1: 1, currentAddressLine1: 1,
      permanentAddressPostal: 1, currentAddressPostal: 1,
      bankPrimary: 4,
    };
    const firstKey = Object.keys(e)[0];
    if (firstKey.startsWith('contact_')) goTab(3);
    else if (tabMap[firstKey] !== undefined) goTab(tabMap[firstKey]);
  }
  return Object.keys(e).length === 0;
};

  // ─── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validate()) return;
    setSub(true);
    setApiError(null);

    try {
      const tinErr = await checkTinUnique(form.tinNo);
      if (tinErr) {
        setErrors((e) => ({ ...e, tinNo: tinErr }));
        showToast(tinErr, 'error');
        goTab(0);
        setSub(false);
        return;
      }
    } catch (err) {
      showToast('Could not check whether the TIN No is already used. Please try again.', 'error');
      setSub(false);
      return;
    }

    try {
      const reportName = fmt(form.reportName) ||
        [form.firstName, form.middleName, form.lastName].filter(Boolean).join(' ');

      const filledContacts = contacts.filter(contactFilled);
      const firstContact = filledContacts[0];

      const payload = {
        ActionType: 1,
        p_hedServiceNo:            fmt(form.serviceNo),
        p_hedEpfNo:                fmt(form.epfNo),
        p_hedEtfNo:                fmt(form.epfNo),
        p_TinNo:                   fmt(form.tinNo),
        p_hedTitle:                fmt(form.title) || 'Mr.',
        p_hedFirstName:            fmt(form.firstName),
        p_hedMiddleName:           fmt(form.middleName),
        p_hedLastName:             fmt(form.lastName),
        p_hedInitials:             fmt(form.initials),
        p_hedReportName:           reportName,
        p_hedGender:               fmt(form.gender),
        p_hedDateOfBirth:          fmt(form.dateOfBirth),
        // A typed lowercase v is sent as a capital V.
        p_hedNicNo:                normaliseNic(form.nicNo),
        p_hedPassportNo:           fmt(form.passportNo),
        p_hedMaritalStatus:        fmt(form.maritalStatus),
        p_hedReligion:             fmt(form.religion),
        p_hedNationality:          '1',
        p_hedBloodGroup: form.bloodGroup ? String(form.bloodGroup) : null,
        // Blank means not stated — 0 is a real answer, so it is sent as 0.
        p_NoOfChildren:            form.noOfChildren === '' ? null : Number(form.noOfChildren),
        p_Distance:                fmt(form.distance),

        p_hedMobileNo:             fmt(form.mobileNo),
        p_hedHomeNo:               fmt(form.homeNo),
        p_hedOfficeNo:             fmt(form.officeNo),
        p_hedPersonalEmail:        fmt(form.personalEmail),
        p_hedCompanyEmail:         fmt(form.companyEmail),

        p_hedDepartmentId:         form.departmentId   ? Number(form.departmentId)   : null,
        p_hddDivCode:              fmt(form.divisionCode),
        p_hedLocationId:           form.locationId     ? Number(form.locationId)     : null,
        p_hedDesignationId:        form.designationId  ? Number(form.designationId)  : null,
        p_hedGradeId:              form.gradeId        ? Number(form.gradeId)        : null,
        p_hedEmploymentCategoryId: form.employmentCategoryId ? Number(form.employmentCategoryId) : null,
        p_hedShiftId:              form.shiftId        ? Number(form.shiftId)        : null,

        p_hedDateOfJoining:        fmt(form.dateOfJoining),
        // Contract: the four career dates are hidden, so they are sent blank,
        // and the Termination Date is sent instead.
        p_RecruitmentDate:         isContract ? '' : fmt(form.recruitmentDate),
        p_PromotedDate:            isContract ? '' : fmt(form.promotedDate),
        p_PermanentDate:           isContract ? '' : fmt(form.permanentDate),
        p_RetirementDate:          isContract ? '' : fmt(form.retirementDate),
        p_termination_date:        isContract ? fmt(form.terminationDate) : '',
        p_hedConfirmationDate:     '',
        p_hedNoticePeriodDays:     form.noticePeriodDays ? Number(form.noticePeriodDays) : null,
        p_OT:               form.otRate ? Number(form.otRate) : null,
        p_OT_Type:                 fmt(form.otType) || null,
        p_Employee_Type:           fmt(form.employeeType) || null,
        p_hedReportingEmployeeId:  form.reportingEmployeeId ? Number(form.reportingEmployeeId) : null,

        p_hedIsSystemUser:         false,
        p_hedCreatedBy:            getLoggedInServiceNo(),
        p_hedIsActive:             true,
        p_hedRemarks:              fmt(form.remarks),
        p_hedGroup:                fmt(form.group),
        p_hedTotalExperiance:      fmt(form.totalExperience),
        p_hedCostCenter:           fmt(form.costCenter),

        // Permanent address
        p_heaAddressType:          'P',
        p_heaAddressLine1:         fmt(form.permanentAddressLine1),
        p_heaAddressLine2:         fmt(form.permanentAddressLine2),
        p_heaAddressLine3:         fmt(form.permanentAddressLine3),
        p_heaCity:                 fmt(form.permanentAddressCity),
        // Region is NOT sent here — it belongs to an address row, so it goes
        // with the AddEmployeeAddress call below.
        p_heaCountryId:            null,
        p_heaPostalCode:           fmt(form.permanentAddressPostal),
        p_heaIsPrimary:            true,
        p_heaCreatedBy:            getLoggedInServiceNo(),
        p_heaIsActive:             true,
        p_heaRemarks:              '',

        // Bank
        p_hebBankId:               form.bankId   ? Number(form.bankId)   : null,
        p_hebBranchId:             form.branchId ? Number(form.branchId) : null,
        // Kept in step with the AddBankDetails calls below: the holder name is
        // the employee's initials, and the type comes from the first bank row.
        p_hebAccountName:          fmt(form.initials),
        p_hebAccountNumber:        fmt(form.accountNumber),
        p_hebAccountType:          bankAccounts[0]?.accountType || DEFAULT_ACCOUNT_TYPE,
        p_hebIsPrimary:            true,
        p_hebCreatedBy:            getLoggedInServiceNo(),
        p_hebIsActive:             true,
        p_hebRemarks:              '',

        // New employees always start unapproved — an approver clears them later.
        p_isApproved:              NEW_EMPLOYEE_IS_APPROVED,

        // Mirrors the first filled contact; every contact is also saved
        // through AddEmployeeContact below.
        p_hecContactName:          fmt(firstContact?.name)         || 'N/A',
        p_hecContactNumber:        fmt(firstContact?.number)       || '',
        p_hecRelationship:         fmt(firstContact?.relationship) || 'Other',
        p_hecContactType:          firstContact ? contactTypeFor(firstContact.isEmergency) : CONTACT_TYPE_CONTACT,
        p_hecAddress:              fmt(firstContact?.address)      || '',
        p_hecCreatedBy:            getLoggedInServiceNo(),
        p_hecIsActive:             true,
        p_hecRemarks:              '',

        p_heqQualificationTypeId:  1,
        p_heqQualificationName:    '',
        p_heqFieldOfStudy:         '',
        p_heqInstitute:            '',
        p_heqCountry:              'Sri Lanka',
        p_heqStartDate:            null,
        p_heqCompletedDate:        null,
        p_heqResult:               '',
        p_heqGrade:                '',
        p_heqGPA:                  '',
        p_heqCertificateNo:        '',
        p_heqAttachment:           '',
        p_heqIsHighest:            true,
        p_heqCreatedBy:            getLoggedInServiceNo(),
        p_heqIsActive:             true,
        p_heqRemarks:              '',
      };

      // 1. Add Employee
      const addRes = await employeeService.AddEmployee(payload);

      if (addRes?.data) {
        const resData = addRes.data;
        if (resData.StatusCode && resData.StatusCode !== 200 && resData.StatusCode !== 201 && resData.StatusCode !== 204) {
          throw new Error(resData.Message || resData.message || resData.Error || 'Failed to save new employee on server.');
        }
        if (resData.Success === false || resData.isSuccess === false) {
          throw new Error(resData.Message || resData.message || resData.Error || 'Failed to save new employee on server.');
        }
      }

      let employeeId = null;
      if (addRes?.data) {
        const rs = addRes.data.ResultSet ?? addRes.data.resultSet ?? addRes.data;
        if (typeof rs === 'number') employeeId = rs;
        else if (rs) employeeId = rs.EmployeeId ?? rs.employeeId ?? rs.HedEmployeeId ?? rs.HED_EMPLOYEE_ID ?? null;
      }
      if (!employeeId) {
        try {
          const listRes = await employeeService.GetEmployeeList();
          const list = extractList(listRes);
          const match = list.find(r => {
            const svcNo = r.HedServiceNo ?? r.ServiceNo ?? r.hedServiceNo ?? r.serviceNo ?? r.HED_SERVICE_NO;
            return String(svcNo) === fmt(form.serviceNo);
          });
          if (match) employeeId = match.HedEmployeeId ?? match.EmployeeId ?? match.hedEmployeeId ?? match.employeeId ?? match.Id ?? match.id ?? match.HED_EMPLOYEE_ID;
        } catch { /* ignore */ }
      }

 
      if (employeeId && (files.profilePic || files.nicDocument || files.birthCertificate || files.qualificationCertificate || files.traineeCertificate || files.otherCertificate || files.jobDescription)) {
        const docFormData = new FormData();
        if (files.profilePic) docFormData.append('ProfilePic', files.profilePic);
        if (files.nicDocument) docFormData.append('NIC', files.nicDocument);
        if (files.birthCertificate) docFormData.append('BirthCertificate', files.birthCertificate);
        if (files.qualificationCertificate) docFormData.append('QualificationCertificate', files.qualificationCertificate);
        if (files.traineeCertificate) docFormData.append('TraineeCertificate', files.traineeCertificate);
        if (files.otherCertificate) docFormData.append('OtherCertificate', files.otherCertificate);
        if (files.jobDescription) docFormData.append('JobDescription', files.jobDescription);

        await employeeService.UploadEmployeeDocs(employeeId, docFormData)
          .catch(err => console.error('[UploadEmployeeDocs] error:', err));
        clearAuthedFileCache();
      }

    
      const hasPermanent = !!(form.permanentAddressLine1 || form.permanentAddressCity);
      const hasCurrent   = !!(form.currentAddressLine1 || form.currentAddressCity);
      const regionFields = {
        p_heaProvinceId:     fmt(form.regionProvince) || null,
        p_heaDistrictId:     fmt(form.regionDistrict) || null,
        p_heaElectorateCode: fmt(form.regionElectorate) || null,
        // "Staion" is the backend's spelling — kept verbatim.
        p_heaStaionCode:     fmt(form.regionPoliceStation) || null,
        p_heaGramaCode:      fmt(form.regionGrama) || null,
      };
      const regionCarrier = hasPermanent ? 'P' : (hasCurrent ? 'C' : null);

      if (employeeId && hasPermanent) {
        await employeeService.AddEmployeeAddress({
          p_hedEmployeeId:  employeeId,
          p_heaCreatedBy:   getLoggedInServiceNo(),
          p_isActive:       'A',
          p_remarks:        '',
          p_heaAddressType: 'P',
          p_heaAddressLine1: fmt(form.permanentAddressLine1),
          p_heaAddressLine2: fmt(form.permanentAddressLine2),
          p_heaAddressLine3: fmt(form.permanentAddressLine3),
          p_heaCity:         fmt(form.permanentAddressCity),
          p_heaPostalCode:   fmt(form.permanentAddressPostal),
          ...(regionCarrier === 'P' ? regionFields : {}),
        }).catch(err => console.error('[AddEmployeeAddress - Permanent] error:', err));
      }

      if (employeeId && hasCurrent) {
        await employeeService.AddEmployeeAddress({
          p_hedEmployeeId:  employeeId,
          p_heaCreatedBy:   getLoggedInServiceNo(),
          p_isActive:       'A',
          p_remarks:        '',
          p_heaAddressType: 'C',
          p_heaAddressLine1: fmt(form.currentAddressLine1),
          p_heaAddressLine2: fmt(form.currentAddressLine2),
          p_heaAddressLine3: fmt(form.currentAddressLine3),
          p_heaCity:         fmt(form.currentAddressCity),
          p_heaPostalCode:   fmt(form.currentAddressPostal),
          ...(regionCarrier === 'C' ? regionFields : {}),
        }).catch(err => console.error('[AddEmployeeAddress - Current] error:', err));
      }

      if (employeeId && extraAddresses.length > 0) {
        await Promise.all(
          extraAddresses
            .filter(addr => addr.line1 || addr.line2 || addr.line3 || addr.city)
            .map((addr, idx) =>
              employeeService.AddEmployeeAddress({
                p_hedEmployeeId:  employeeId,
                p_heaCreatedBy:   getLoggedInServiceNo(),
                p_isActive:       'A',
                p_remarks:        fmt(addr.label),
                p_heaAddressType: extraAddressTypeCode(idx),
                p_heaAddressLine1: fmt(addr.line1),
                p_heaAddressLine2: fmt(addr.line2),
                p_heaAddressLine3: fmt(addr.line3),
                p_heaCity:         fmt(addr.city),
                p_heaPostalCode:   fmt(addr.postal),
              }).catch(() => {})
            )
        );
      }

      // 4. Qualifications — sequential loop, one AddEmployeeQualification call per row, real error logging.
      const validQualifications = qualifications.filter(q => fmt(q.name));
      if (employeeId && validQualifications.length > 0) {
        for (let idx = 0; idx < validQualifications.length; idx++) {
          const q = validQualifications[idx];
          try {
            const qualRes = await employeeService.AddEmployeeQualification({
              p_hedEmployeeId:          employeeId,
              p_heqQualificationTypeId: 1,
              p_heqQualificationName:   fmt(q.name),
              p_heqFieldOfStudy:        fmt(q.fieldOfStudy),
              p_heqInstitute:           fmt(q.institute),
              p_heqCountry:             'Sri Lanka',
              p_heqStartDate:           null,
              p_heqCompletedDate:       null,
              p_heqResult:              fmt(q.result),
              p_heqGrade:               fmt(q.grade),
              p_heqGPA:                 fmt(q.gpa),
              p_heqCertificateNo:       '',
              p_heqAttachment:          '',
              p_heqIsHighest:           idx === 0,
              p_heqCreatedBy:           getLoggedInServiceNo(),
              p_heqIsActive:            true,
              p_heqRemarks:             '',
            });
            console.log(`[AddEmployeeQualification] row ${idx + 1} ("${q.name}") response:`, qualRes?.data);
          } catch (err) {
            console.error(`[AddEmployeeQualification] row ${idx + 1} ("${q.name}") FAILED:`, err?.response?.data || err);
          }
        }
      }
      // 6. Bank Details (AddBankDetails API)
      const validBanks = bankAccounts.filter(b => b.bankId && b.branchId && b.accountNumber);
      if (employeeId && validBanks.length > 0) {
        await Promise.all(
          validBanks.map((b, idx) =>
            employeeService.AddBankDetails({
              p_hebBankId: Number(b.bankId),
              p_hebBranchId: Number(b.branchId),
              // Account holder name is always the employee's initials.
              p_hebAccountName: fmt(form.initials),
              p_hebAccountNumber: fmt(b.accountNumber),
              p_hebAccountType: b.accountType || DEFAULT_ACCOUNT_TYPE,
              p_hebIsPrimary: b.isPrimary ? 1 : 0,
              p_hebCreatedBy: getLoggedInServiceNo(),
              p_hedEmployeeId: Number(employeeId),
              // HEB_IS_APPROVED is NOT NULL, and a new account is never
              // pre-approved — only the Approve tick sends 1.
              p_isApproved: NEW_BANK_IS_APPROVED,
            }).catch(err => console.error('[AddBankDetails] error:', err))
          )
        );
      }

      // 5a. Employment-detail workaround — AddEmployee doesn't reliably persist Department/Category
      if (employeeId && (form.departmentId || form.employmentCategoryId || form.gradeId || form.locationId || form.shiftId)) {
        const fixUpDiff = { p_hedServiceNo: fmt(form.serviceNo) };
        if (form.departmentId)         fixUpDiff.p_hedDepartmentId = Number(form.departmentId);
        if (form.divisionCode)         fixUpDiff.p_hddDivCode = fmt(form.divisionCode);
        if (form.employmentCategoryId) fixUpDiff.p_hedEmploymentCategoryId = Number(form.employmentCategoryId);
        if (form.gradeId)              fixUpDiff.p_hedGradeId = Number(form.gradeId);
        if (form.locationId)           fixUpDiff.p_hedLocationId = Number(form.locationId);
        if (form.shiftId)              fixUpDiff.p_hedShiftId = Number(form.shiftId);

        await employeeService.UpdateEmployee(fixUpDiff)
          .catch(err => console.error('[UpdateEmployee - employment-detail workaround] error:', err));
      }

      // 6a. Contacts (AddEmployeeContact API) — one call per filled contact.
      // Ticked contacts go as 'Emergency', the rest as 'Contact'.
      if (employeeId) {
        for (const c of filledContacts) {
          await employeeService.AddEmployeeContact({
            p_hedEmployeeId: employeeId,
            p_hecContactName: fmt(c.name) || 'N/A',
            p_hecContactNumber: fmt(c.number) || '',
            p_hecRelationship: fmt(c.relationship) || 'Other',
            p_hecContactType: contactTypeFor(c.isEmergency),
            p_hecAddress: fmt(c.address) || '',
            p_hecCreatedBy: getLoggedInServiceNo(),
            p_hecIsActive: true,
            p_hecRemarks: '',
          }).catch(err => console.error('[AddEmployeeContact] error:', err));
        }
      }

      showToast('Employee added successfully!', 'success');
      setForm(EMPTY);
      setFiles(EMPTY_FILES);
      setExtraAddresses([]);
      setContacts([newContact('contact-0')]);
      setBankAccounts([{ id: 'bank-0', bankId: '', branchId: '', accountNumber: '', accountType: DEFAULT_ACCOUNT_TYPE, isPrimary: true }]);
      setQualifications([{ id: 'qual-0', name: '', fieldOfStudy: '', institute: '', result: '', grade: '', gpa: '' }]);
      dispatch(fetchAllEmployees());
      setTimeout(() => { if (onSuccess) onSuccess(); onClose(); }, 1500);
    } catch (err) {
      const msg = err?.response?.data?.Message || err?.response?.data?.message || err?.message || 'Failed to save employee.';
      showToast(msg, 'error');
      setApiError(msg);
    } finally {
      setSub(false);
    }
  };

  const goTab = (t) => { setTab(t); setVisited(v => new Set([...v, t])); };
  const goNext = () => goTab(Math.min(tab + 1, TABS.length - 1));
  const goPrev = () => goTab(Math.max(tab - 1, 0));

  // ─── Tab Panels ───────────────────────────────────────────────────────────
  const renderTab = () => {
    switch (tab) {

      // ── PERSONAL ──────────────────────────────────────────────────────────
      case 0: return (
        <div className="space-y-4">
          <SecLabel label="Identification" />
          <G4>
            <Field label="Staff ID" required error={errors.serviceNo}>
              <Inp id="serviceNo" value={form.serviceNo} onChange={v => set('serviceNo', sanitiseStaffId(v))} placeholder="Staff ID (max 10)" error={errors.serviceNo} />
            </Field>
            <Field label="EPF No" required error={errors.epfNo}>
              <Inp value={form.epfNo} onChange={v => set('epfNo', sanitiseEpf(v))} placeholder="EPF No (max 5)" error={errors.epfNo} />
            </Field>
            <Field label="TIN No" error={errors.tinNo}>
              {/* Numbers only — anything else typed or pasted is dropped. */}
              <Inp value={form.tinNo} onChange={v => set('tinNo', String(v ?? '').replace(/\D/g, ''))} placeholder="TIN No" error={errors.tinNo} />
            </Field>
            <Field label="NIC No" required error={errors.nicNo}>
              <Inp value={form.nicNo} onChange={v => set('nicNo', sanitiseNic(v))} placeholder="200012345678 or 200012345V" error={errors.nicNo} />
            </Field>
            <Field label="Passport No">
              <Inp value={form.passportNo} onChange={v => set('passportNo', v)} placeholder="P123456" />
            </Field>
          </G4>

          <SecLabel label="Personal Details" />
          <G4>
            <Field label="Title">
              <Sel value={form.title} onChange={v => set('title', v)} options={TITLES.map(t => ({ value: t, label: t }))} />
            </Field>
            <Field label="Gender">
              <Sel value={form.gender} onChange={v => set('gender', v)} options={GENDERS} />
            </Field>
            <Field label="First Name" required error={errors.firstName}>
              <Inp value={form.firstName} onChange={v => set('firstName', v)} placeholder="First name" error={errors.firstName} />
            </Field>
            <Field label="Middle Name">
              <Inp value={form.middleName} onChange={v => set('middleName', v)} placeholder="Middle name" />
            </Field>
          </G4>
          <G4>
            <Field label="Last Name" required error={errors.lastName}>
              <Inp value={form.lastName} onChange={v => set('lastName', v)} placeholder="Last name" error={errors.lastName} />
            </Field>
            <Field label="Initials" required error={errors.initials}>
              <Inp value={form.initials} onChange={v => set('initials', v)} placeholder="P.K.N." error={errors.initials} />
            </Field>
            <Field label="Report Name">
              <Inp value={form.reportName} onChange={v => set('reportName', v)} placeholder="Auto-filled if blank" />
            </Field>
          </G4>
          <G4>
            <Field label="Date of Birth" required error={errors.dateOfBirth}>
              <IsoDateInput
                value={form.dateOfBirth}
                onChange={v => set('dateOfBirth', v)}
                max={maxDobIso()}
                className={iCls(errors.dateOfBirth)}
              />
            </Field>
            <Field label="Marital Status">
              <Sel value={form.maritalStatus} onChange={v => set('maritalStatus', v)} options={MARITAL_STATUSES} />
            </Field>
            <Field label="Religion">
              <Sel value={form.religion} onChange={v => set('religion', v)} options={RELIGIONS} placeholder="Select" />
            </Field>
            <Field label="Blood Group">
              <Sel value={form.bloodGroup} onChange={v => set('bloodGroup', v)} options={BLOOD_GROUPS} placeholder="Select" />
            </Field>
            {/* Whole children only — digits are kept and everything else is
                dropped as it is typed, so the field can't hold "2.5" or "-1". */}
            <Field label="No of Children">
              <Inp
                value={form.noOfChildren}
                onChange={v => set('noOfChildren', String(v).replace(/[^0-9]/g, '').slice(0, 2))}
                placeholder="0"
              />
            </Field>
          </G4>

          <SecLabel label="Contact Details" />
          <G3>
            <Field label="Mobile No" required>
              <Inp value={form.mobileNo} onChange={v => set('mobileNo', sanitisePhone(v))} placeholder="0771234567" error={errors.mobileNo} />
            </Field>
            <Field label="Home Phone">
              <Inp value={form.homeNo} onChange={v => set('homeNo', sanitisePhone(v))} placeholder="0112345678" error={errors.homeNo} />
            </Field>
            <Field label="Office Phone">
              <Inp value={form.officeNo} onChange={v => set('officeNo', sanitisePhone(v))} placeholder="0112345678" error={errors.officeNo} />
            </Field>
            <Field label="Company Email" error={errors.companyEmail}>
              <Inp type="email" value={form.companyEmail} onChange={v => set('companyEmail', v)} placeholder="name@company.com" error={errors.companyEmail} />
            </Field>
            <Field label="Personal Email" error={errors.personalEmail}>
              <Inp type="email" value={form.personalEmail} onChange={v => set('personalEmail', v)} placeholder="name@gmail.com" error={errors.personalEmail} />
            </Field>
          </G3>

          <SecLabel label="Profile Photo & Documents" />
          <div className="flex items-center gap-5 p-4 bg-gray-50 border border-gray-200 rounded-xl mb-4">
            {/* Circular Avatar Preview */}
            <div className="relative shrink-0">
              <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-white shadow-md ring-4 ring-primary-100 bg-gray-200 flex items-center justify-center">
                {files.profilePic ? (
                  <img
                    src={URL.createObjectURL(files.profilePic)}
                    alt="Profile Preview"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User size={38} className="text-gray-400" />
                )}
              </div>
              <label
                htmlFor="file-profile-pic-avatar"
                className="absolute bottom-0 right-0 bg-primary-600 hover:bg-primary-700 text-white p-2 rounded-full shadow-lg cursor-pointer transition-transform hover:scale-110"
                title="Upload Profile Picture"
              >
                <Upload size={13} />
              </label>
              <input
                id="file-profile-pic-avatar"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={e => setFile('profilePic', e.target.files?.[0] || null)}
              />
            </div>

            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-gray-800">Profile Picture</p>
              <p className="text-[11px] text-gray-500 mt-0.5 truncate">
                {files.profilePic ? files.profilePic.name : 'Upload employee profile photo (PNG, JPG or WEBP)'}
              </p>
              <div className="flex items-center gap-3 mt-2.5">
                <label
                  htmlFor="file-profile-pic-avatar"
                  className="h-9 inline-flex items-center px-3 bg-primary-50 border border-primary-200 text-primary-700 rounded-lg text-xs font-semibold hover:bg-primary-100 transition-colors cursor-pointer"
                >
                  {files.profilePic ? 'Change Photo' : 'Upload Photo'}
                </label>
                {files.profilePic && (
                  <button
                    type="button"
                    onClick={() => setFile('profilePic', null)}
                    className="text-xs text-red-500 hover:text-red-600 font-medium flex items-center gap-1"
                  >
                    <Trash2 size={12} /> Remove
                  </button>
                )}
              </div>
            </div>
          </div>

          <G3>
            <FileField
              label="NIC Copy"
              file={files.nicDocument}
              onChange={f => setFile('nicDocument', f)}
              accept="image/*,.pdf"
            />
            <FileField
              label="Birth Certificate"
              file={files.birthCertificate}
              onChange={f => setFile('birthCertificate', f)}
              accept="image/*,.pdf"
            />
            <FileField
              label="Qualification Certificate"
              file={files.qualificationCertificate}
              onChange={f => setFile('qualificationCertificate', f)}
              accept="image/*,.pdf"
            />
            <FileField
              label="Trainee Certificate"
              file={files.traineeCertificate}
              onChange={f => setFile('traineeCertificate', f)}
              accept="image/*,.pdf"
            />
            <FileField
              label="Other Certificates"
              file={files.otherCertificate}
              onChange={f => setFile('otherCertificate', f)}
              accept="image/*,.pdf"
            />
            <FileField
              label="Job Description"
              file={files.jobDescription}
              onChange={f => setFile('jobDescription', f)}
              accept="image/*,.pdf"
            />
          </G3>
        </div>
      );

      // ── ADDRESS ────────────────────────────────────────────────────────────
      case 1: return (
        <div className="space-y-4">
          {/* ── Region ──*/}
          <SecLabel label="Region" />
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-x-4 gap-y-3">
            <Field label="Province">
              <Sel
                value={form.regionProvince}
                onChange={v => setForm(f => ({ ...f, regionProvince: v, regionDistrict: '', regionElectorate: '' }))}
                options={rgProvinces.map(r => ({ value: RG.provCode(r), label: RG.provName(r) || RG.provCode(r) })).filter(o => o.value)}
                loading={ddLoading}
                placeholder="Select Province"
              />
            </Field>
            <Field label="District">
              <Sel
                value={form.regionDistrict}
                onChange={v => setForm(f => ({ ...f, regionDistrict: v, regionElectorate: '' }))}
                options={rgFilterDistricts(rgDistricts, form.regionProvince).map(r => ({ value: RG.distCode(r), label: RG.distName(r) || RG.distCode(r) })).filter(o => o.value)}
                loading={ddLoading}
                placeholder={form.regionProvince ? 'Select District' : 'Select a province first'}
              />
            </Field>
            <Field label="Electorate">
              <Sel
                value={form.regionElectorate}
                onChange={v => setForm(f => ({ ...f, regionElectorate: v }))}
                options={rgFilterElectorates(rgElectorates, form.regionProvince, form.regionDistrict).map(r => ({ value: RG.elecCode(r), label: RG.elecArea(r) || RG.elecCode(r) })).filter(o => o.value)}
                loading={ddLoading}
                placeholder={form.regionDistrict ? 'Select Electorate' : 'Select a district first'}
              />
            </Field>
            <Field label="Police Station">
              <Sel
                value={form.regionPoliceStation}
                onChange={v => set('regionPoliceStation', v)}
                options={rgStationOptions(rgStations)}
                loading={ddLoading}
                placeholder="Select Police Station"
              />
            </Field>
            <Field label="Grama Division">
              <Sel
                value={form.regionGrama}
                onChange={v => set('regionGrama', v)}
                options={rgGramas.map(r => ({ value: RG.gramaCode(r), label: RG.gramaName(r) || RG.gramaCode(r) })).filter(o => o.value)}
                loading={ddLoading}
                placeholder="Select Grama Division"
              />
            </Field>
          </div>

          <SecLabel label="Permanent Address" />
          <G2>
            <Field label="Address Line 1">
              <Inp value={form.permanentAddressLine1} onChange={v => set('permanentAddressLine1', v)} placeholder="No / Street" />
            </Field>
            <Field label="Address Line 2">
              <Inp value={form.permanentAddressLine2} onChange={v => set('permanentAddressLine2', v)} placeholder="Area / Building" />
            </Field>
          </G2>
          <G3>
            <Field label="Address Line 3">
              <Inp value={form.permanentAddressLine3} onChange={v => set('permanentAddressLine3', v)} placeholder="Town" />
            </Field>
            <Field label="City">
              <Inp value={form.permanentAddressCity} onChange={v => set('permanentAddressCity', v)} placeholder="City" />
            </Field>
            <Field label="Postal Code">
              <Inp value={form.permanentAddressPostal} onChange={v => set('permanentAddressPostal', sanitisePostal(v))} placeholder="5 digits" error={errors.permanentAddressPostal} />
            </Field>
          </G3>

          <SecLabel label="Current Address" />
          <G2>
            <Field label="Address Line 1">
              <Inp value={form.currentAddressLine1} onChange={v => set('currentAddressLine1', v)} placeholder="No / Street" />
            </Field>
            <Field label="Address Line 2">
              <Inp value={form.currentAddressLine2} onChange={v => set('currentAddressLine2', v)} placeholder="Area / Building" />
            </Field>
          </G2>
          <G4>
            <Field label="Address Line 3">
              <Inp value={form.currentAddressLine3} onChange={v => set('currentAddressLine3', v)} placeholder="Town" />
            </Field>
            <Field label="City">
              <Inp value={form.currentAddressCity} onChange={v => set('currentAddressCity', v)} placeholder="City" />
            </Field>
            <Field label="Postal Code">
              <Inp value={form.currentAddressPostal} onChange={v => set('currentAddressPostal', sanitisePostal(v))} placeholder="5 digits" error={errors.currentAddressPostal} />
            </Field>
            {/* Distance from the current address to the workplace. DECIMAL on
                the backend, so one decimal point is allowed but a second one, a
                letter or a sign is not. */}
            <Field label="Distance (km)">
              <Inp value={form.distance} onChange={v => set('distance', sanitiseDecimal(v))} placeholder="e.g. 12.5" />
            </Field>
          </G4>

          <SecLabel label="Additional Addresses" />
          <div className="space-y-3">
            {extraAddresses.map((addr, idx) => (
              <div key={addr.id} className="relative border border-gray-200 rounded p-3 bg-gray-50/60 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                    Address Type Code: {extraAddressTypeCode(idx)}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeExtraAddress(addr.id)}
                    className="text-gray-400 hover:text-red-500 transition-colors"
                    title="Remove address"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                <G2>
                  <Field label="Address Label">
                    <Inp value={addr.label} onChange={v => updateExtraAddress(addr.id, 'label', v)} placeholder="Work Address" />
                  </Field>
                  <Field label="City">
                    <Inp value={addr.city} onChange={v => updateExtraAddress(addr.id, 'city', v)} placeholder="City" />
                  </Field>
                </G2>
                <G2>
                  <Field label="Address Line 1">
                    <Inp value={addr.line1} onChange={v => updateExtraAddress(addr.id, 'line1', v)} placeholder="No / Street" />
                  </Field>
                  <Field label="Address Line 2">
                    <Inp value={addr.line2} onChange={v => updateExtraAddress(addr.id, 'line2', v)} placeholder="Area / Building" />
                  </Field>
                </G2>
                <G2>
                  <Field label="Address Line 3">
                    <Inp value={addr.line3} onChange={v => updateExtraAddress(addr.id, 'line3', v)} placeholder="Town" />
                  </Field>
                  <Field label="Postal Code">
                    <Inp value={addr.postal} onChange={v => updateExtraAddress(addr.id, 'postal', sanitisePostal(v))} placeholder="5 digits" />
                  </Field>
                </G2>
              </div>
            ))}

            <button
              type="button"
              onClick={addExtraAddress}
              className="flex items-center gap-1.5 h-9 px-3 border border-dashed border-primary-300 text-primary-600 rounded-lg text-xs font-semibold hover:bg-primary-50 transition-colors w-fit"
            >
              <Plus size={13} /> Add Address
            </button>
          </div>

        </div>
      );

      // ── EMPLOYMENT ─────────────────────────────────────────────────────────
      case 2: return (
        <div className="space-y-4">
  <SecLabel label="Employment Details" />

  <G3>
    <Field label="Designation" required error={errors.designationId}>
      <Sel
        id="designationId"
        value={form.designationId}
        onChange={v => set('designationId', v)}
        options={designations}
        loading={ddLoading}
        placeholder="Select Designation"
        error={errors.designationId}
      />
    </Field>

    <Field label="Division" required error={errors.divisionCode}>
      <Sel
        value={form.divisionCode}
        onChange={v => set('divisionCode', v)}
        options={divisions}
        loading={ddLoading}
        placeholder="Select Division"
        error={errors.divisionCode}
      />
    </Field>

    {/* Only the chosen division's active departments (HDD_DIV_CODE). */}
    <Field label="Department" required error={errors.departmentId}>
      <Sel
        value={form.departmentId}
        onChange={v => set('departmentId', v)}
        options={form.divisionCode ? departments.filter(d => d.div === form.divisionCode) : []}
        loading={ddLoading}
        placeholder={form.divisionCode ? 'Select Department' : 'Select Division first'}
        error={errors.departmentId}
      />
    </Field>
  </G3>

  <G2>
    <Field label="Location" required error={errors.locationId}>
      <Sel
        value={form.locationId}
        onChange={v => set('locationId', v)}
        options={locations}
        loading={ddLoading}
        placeholder="Select Location"
        error={errors.locationId}
      />
    </Field>

    <Field label="Grade (Work Category)" required error={errors.gradeId}>
      <Sel
        value={form.gradeId}
        onChange={v => set('gradeId', v)}
        options={grades}
        loading={ddLoading}
        placeholder="Select Grade"
        error={errors.gradeId}
      />
    </Field>
  </G2>

  <G2>
<Field label="Employment Category (Present Employer)" required error={errors.employmentCategoryId}>
      <Sel
        value={form.employmentCategoryId}
        onChange={v => set('employmentCategoryId', v)}
        options={categories}
        loading={ddLoading}
        placeholder="Select Category"
        error={errors.employmentCategoryId}
      />
    </Field>

    <Field label="Shift" required error={errors.shiftId}>
      <Sel
        value={form.shiftId}
        onChange={v => set('shiftId', v)}
        options={shifts}
        loading={ddLoading}
        placeholder="Select Shift"
        error={errors.shiftId}
      />
    </Field>
  </G2>

  <G2>
    <Field label="Employee Type">
      <Sel
        value={form.employeeType}
        onChange={v => set('employeeType', v)}
        options={EMPLOYEE_TYPES}
        placeholder="Select Employee Type"
      />
    </Field>

    <Field label="OT Type">
      <Sel
        value={form.otType}
        onChange={v => set('otType', v)}
        options={OT_TYPES}
        placeholder="Select OT Type"
      />
    </Field>
  </G2>

  <G3>
    <Field label="Date of Joining" required error={errors.dateOfJoining}>
      <IsoDateInput
        value={form.dateOfJoining}
        onChange={v => set('dateOfJoining', v)}
        max={todayIso()}
        className={iCls(errors.dateOfJoining)}
      />
    </Field>

    <Field label="Notice Period (days)">
      <Inp
        type="number"
        value={form.noticePeriodDays}
        onChange={v => set('noticePeriodDays', v)}
        placeholder="0"
      />
    </Field>

    <Field label="OT Rate (LKR)">
      <Inp
        type="number"
        value={form.otRate}
        onChange={v => set('otRate', v)}
        placeholder="0"
      />
    </Field>
  </G3>

  {/* Career dates. Only recruitment is bounded by today — promotion,
      permanency and retirement can all legitimately be set in the future. */}
  <G4>
    {isContract ? (
      <Field label="Termination Date" required error={errors.terminationDate}>
        <IsoDateInput
          value={form.terminationDate}
          onChange={v => set('terminationDate', v)}
          className={iCls(errors.terminationDate)}
        />
      </Field>
    ) : (
      <>
        <Field label="Recruitment Date">
          <IsoDateInput
            value={form.recruitmentDate}
            onChange={v => set('recruitmentDate', v)}
            max={todayIso()}
            className={iCls()}
          />
        </Field>

        <Field label="Promoted Date">
          <IsoDateInput
            value={form.promotedDate}
            onChange={v => set('promotedDate', v)}
            className={iCls()}
          />
        </Field>

        <Field label="Permanent Date">
          <IsoDateInput
            value={form.permanentDate}
            onChange={v => set('permanentDate', v)}
            className={iCls()}
          />
        </Field>

        <Field label="Retirement Date">
          <IsoDateInput
            value={form.retirementDate}
            onChange={v => set('retirementDate', v)}
            className={iCls()}
          />
        </Field>
      </>
    )}
  </G4>

  <G2>
    <Field label="Total Experience" error={errors.totalExperience}>
      <ExperienceInput
        value={form.totalExperience}
        onChange={v => set('totalExperience', v)}
        inputClassName={iCls(errors.totalExperience)}
      />
    </Field>

    <Field label="Reporting Employee">
      <Sel
        value={form.reportingEmployeeId}
        onChange={v => set('reportingEmployeeId', v)}
        options={employees}
        loading={ddLoading}
        placeholder="Select Reporting Employee"
      />
    </Field>
  </G2>

  <G2>
    <Field label="Cost Center">
      <Inp
        value={form.costCenter}
        onChange={v => set('costCenter', v)}
        placeholder="Cost Center"
      />
    </Field>

    <Field label="Group">
      <Inp
        value={form.group}
        onChange={v => set('group', v)}
        placeholder="Group"
      />
    </Field>
  </G2>
</div>
      );

      // ── CONTACT & EMERGENCY ────────────────────────────────────────────────
      case 3: return (
        <div className="space-y-4">
          <SecLabel label="Contact Details" />
          <div className="space-y-3">
            {contacts.map((c, idx) => (
              <div key={c.id} className="relative border border-gray-200 rounded p-3 bg-gray-50/60 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold text-gray-700">
                    Contact #{idx + 1}
                    {c.isEmergency && (
                      <span className="ml-2 px-1.5 py-0.5 rounded bg-red-50 text-red-600 text-[10px] font-semibold">
                        Emergency
                      </span>
                    )}
                  </span>
                  {contacts.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeContact(c.id)}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                      title="Remove contact"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
                <G3>
                  <Field label="Contact Name">
                    <Inp value={c.name} onChange={v => updateContact(c.id, 'name', v)} placeholder="Full name" />
                  </Field>
                  <Field label="Relationship">
                    <Sel
                      value={c.relationship}
                      onChange={v => updateContact(c.id, 'relationship', v)}
                      options={RELATIONSHIP_OPTS}
                      placeholder="Select"
                    />
                  </Field>
                  <Field label="Contact Number">
                    <Inp
                      value={c.number}
                      onChange={v => updateContact(c.id, 'number', sanitisePhone(v))}
                      placeholder="0771234567"
                      error={errors[`contact_${c.id}_number`]}
                    />
                  </Field>
                </G3>
                <G2>
                  <Field label="Address">
                    <Inp value={c.address} onChange={v => updateContact(c.id, 'address', v)} placeholder="No / Street, City" />
                  </Field>
                </G2>
                <div className="flex items-center gap-2 pt-0.5">
                  <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!c.isEmergency}
                      onChange={e => updateContact(c.id, 'isEmergency', e.target.checked)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-400"
                    />
                    Set as emergency contact
                  </label>
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={addContact}
              className="flex items-center gap-1.5 h-9 px-3 border border-dashed border-primary-300 text-primary-600 rounded-lg text-xs font-semibold hover:bg-primary-50 transition-colors w-fit"
            >
              <Plus size={13} /> Add Contact
            </button>
          </div>
        </div>
      );

      // ── BANK ───────────────────────────────────────────────────────────────
      case 4: return (
        <div className="space-y-4">
          <SecLabel label="Bank Details" />
          {errors.bankPrimary && (
            <p className="text-[11px] text-red-500 flex items-center gap-1 -mt-2">
              <AlertCircle size={11} /> {errors.bankPrimary}
            </p>
          )}
          <div className="space-y-3">
            {bankAccounts.map((b, idx) => {
              const currentBranches = b.bankId ? branches.filter(br => br.bankId === b.bankId) : branches;
              return (
                <div key={b.id} className="relative border border-gray-200 rounded p-3 bg-gray-50/60 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-bold text-gray-700">
                      Bank Account #{idx + 1} {b.isPrimary ? '(Primary)' : ''}
                    </span>
                    {bankAccounts.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeBankAccount(b.id)}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                        title="Remove bank account"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                  <G2>
                    <Field label="Bank">
                      <Sel
                        value={b.bankId}
                        onChange={v => {
                          updateBankAccount(b.id, 'bankId', v);
                          updateBankAccount(b.id, 'branchId', '');
                        }}
                        options={banks}
                        loading={ddLoading}
                        placeholder="Select Bank"
                      />
                    </Field>
                    <Field label="Branch">
                      <Sel
                        value={b.branchId}
                        onChange={v => updateBankAccount(b.id, 'branchId', v)}
                        options={currentBranches}
                        loading={ddLoading}
                        placeholder="Select Branch"
                      />
                    </Field>
                  </G2>
                  <G2>
                    <Field label="Account Number">
                      <Inp
                        value={b.accountNumber}
                        onChange={v => updateBankAccount(b.id, 'accountNumber', v)}
                        placeholder="Account No"
                      />
                    </Field>
                    <Field label="Account Type">
                      <Sel
                        value={b.accountType || DEFAULT_ACCOUNT_TYPE}
                        onChange={v => updateBankAccount(b.id, 'accountType', v || DEFAULT_ACCOUNT_TYPE)}
                        options={ACCOUNT_TYPES}
                        clearable={false}
                      />
                    </Field>
                  </G2>

                  {/* The account holder name is the employee's initials — it is
                      never typed here, so it can't disagree with the record. */}
                  <div className="flex items-center gap-2 text-[11px] text-gray-500">
                    <span className="font-semibold text-gray-600">Account Holder Name:</span>
                    {fmt(form.initials)
                      ? <span className="font-semibold text-gray-800">{fmt(form.initials)}</span>
                      : <span className="italic text-gray-400">set the employee's Initials on the Personal tab</span>}
                  </div>
                  <div className="flex items-center gap-2 pt-0.5">
                    <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!b.isPrimary}
                        onChange={e => updateBankAccount(b.id, 'isPrimary', e.target.checked)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-400"
                      />
                      Primary Account
                    </label>
                  </div>
                </div>
              );
            })}

            <button
              type="button"
              onClick={addBankAccount}
              className="flex items-center gap-1.5 h-9 px-3 border border-dashed border-primary-300 text-primary-600 rounded-lg text-xs font-semibold hover:bg-primary-50 transition-colors w-fit"
            >
              <Plus size={13} /> Add Bank Account
            </button>
          </div>
        </div>
      );

      // ── OTHER ──────────────────────────────────────────────────────────────
      case 5: return (
        <div className="space-y-4">
                    <div className="flex items-center justify-between mb-2 mt-1">
            <SecLabel label="Educational Qualifications" />
            <button
              type="button"
              onClick={addQualification}
              className="flex items-center gap-1 text-[12px] font-semibold text-primary-600 hover:text-primary-800 shrink-0"
            >
              <Plus size={13} /> Add Qualification
            </button>
          </div>
          <div className="space-y-3">
            {qualifications.map((q, idx) => (
              <div key={q.id} className="relative border border-gray-200 rounded-xl p-4 bg-gray-50/60">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[12px] font-bold text-gray-700">Qualification {idx + 1}</span>
                  {qualifications.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeQualification(q.id)}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                      title="Remove qualification"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
                <G2>
                  <Field label="Qualification Name">
                    <Inp value={q.name} onChange={v => updateQualification(q.id, 'name', v)} placeholder="A/L, CIMA, HND" />
                  </Field>
                  <Field label="Result">
                    <Inp value={q.result} onChange={v => updateQualification(q.id, 'result', v)} placeholder=" 3 A's, Passed" />
                  </Field>
                  <Field label="Field of Study">
                    <Inp value={q.fieldOfStudy} onChange={v => updateQualification(q.id, 'fieldOfStudy', v)} placeholder="Science" />
                  </Field>
                  <Field label="Grade">
                    <Inp value={q.grade} onChange={v => updateQualification(q.id, 'grade', v)} placeholder=" A" />
                  </Field>
                  <Field label="Institute">
                    <Inp value={q.institute} onChange={v => updateQualification(q.id, 'institute', v)} placeholder="Institute name" />
                  </Field>
                  <Field label="GPA">
                    <Inp value={q.gpa} onChange={v => updateQualification(q.id, 'gpa', v)} placeholder=" 3.5" />
                  </Field>
                </G2>
              </div>
            ))}
          </div>
          <div className="max-w-md">
            <FileField
              label="Upload Qualification Certificate / Attachment"
              file={files.qualificationCertificate}
              onChange={f => setFile('qualificationCertificate', f)}
              accept="image/*,.pdf"
            />
          </div>
          <SecLabel label="Remarks" />
          <Field label="Remarks">
            <textarea value={form.remarks} onChange={e => set('remarks', e.target.value)} rows={3}
              className="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-xs text-gray-800 outline-none focus:border-primary-500 resize-none bg-white transition-colors"
              placeholder="Additional remarks..." />
          </Field>
        </div>
      );

      default: return null;
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 overflow-y-auto py-4 px-2">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="bg-white w-full max-w-5xl mx-auto my-auto shadow-2xl rounded" style={{ minHeight: 540 }}>

        {/* Title bar */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-gray-700 rounded-t">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-primary-400 rounded-sm flex items-center justify-center">
              <User size={11} className="text-white" />
            </div>
            <span className="text-white text-[13px] font-semibold tracking-wide">Add New Employee</span>
          </div>
          <button onClick={onClose} className="text-gray-300 hover:text-white hover:bg-gray-600 transition-colors p-1 rounded">
            <X size={16} />
          </button>
        </div>

        {/* Loading bar */}
        {ddLoading && (
          <div className="flex items-center gap-2 px-4 py-1.5 bg-primary-50 border-b border-primary-200 text-primary-700 text-[11px]">
            <Loader2 size={11} className="animate-spin" /> Loading dropdown data from server...
          </div>
        )}

        {/* Tab bar */}
        <div className="flex border-b border-gray-200 bg-gray-50">
          {TABS.map(t => {
            const Icon = t.icon;
            const active = tab === t.id;
            const visited = visitedTabs.has(t.id) && !active;
            return (
              <button key={t.id} onClick={() => goTab(t.id)}
                className={`flex items-center gap-1.5 px-5 py-2.5 text-[12px] font-semibold border-b-2 transition-all whitespace-nowrap
                  ${active ? 'border-primary-600 text-primary-700 bg-white' : visited ? 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-100' : 'border-transparent text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
              >
                <Icon size={13} className={active ? 'text-primary-600' : ''} />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        <div className="p-5 overflow-y-auto bg-white" style={{ maxHeight: '62vh' }}>
          {apiError && (
            <div className="flex items-center gap-2 px-4 py-3 mb-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs font-medium">
              <AlertCircle size={14} className="shrink-0 text-red-600" />
              <span className="flex-1">{apiError}</span>
              <button onClick={() => setApiError(null)} className="text-red-400 hover:text-red-600">
                <X size={14} />
              </button>
            </div>
          )}
          {renderTab()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 bg-gray-50 shrink-0">
          <button onClick={onClose}
            className="flex items-center gap-1.5 h-9 px-4 rounded-lg text-xs font-semibold bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            Cancel
          </button>

          <div className="flex items-center gap-2">
            <button onClick={goPrev} disabled={tab === 0}
              className="flex items-center gap-1.5 h-9 px-4 rounded-lg text-xs font-semibold bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
              <ChevronLeft size={13} /> Prev
            </button>

            {tab < TABS.length - 1 ? (
              <button onClick={goNext}
                className="flex items-center gap-2 h-9 px-5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-xs font-semibold transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed">
                Next <ChevronRight size={13} />
              </button>
            ) : (
              <button onClick={handleSubmit} disabled={submitting}
                className="flex items-center gap-2 h-9 px-5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-xs font-semibold transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed">
                {submitting ? <><Loader2 size={13} className="animate-spin" />Saving...</> : <><CheckCircle size={13} />Save Employee</>}
              </button>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideInRight { from { transform: translateX(120px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
      `}</style>
    </div>
  );
}